/**
 * THE company calendar-day rule for the web app (#645, #656, #658, #648/#650/#651).
 *
 * A calendar day — a range bound, a "today", the day a shift or event belongs to — is a day in the COMPANY's
 * configured time zone, never the browser's. A shift in a Berlin company that starts at 21:30 on 31.07. belongs to
 * July for a viewer in Berlin, in New York and in Tokyo alike. The server resolves the same zone
 * (`ISettingService.GetTimeZone()`, default `Europe/Berlin`), and both sides are pinned by the same scenarios in
 * `fixtures/company-day.fixtures.json` (replayed by `company-day.spec.ts` here and by the C# helper's tests).
 *
 * Vocabulary:
 * - an **instant** is a point in time (`Date` / epoch milliseconds), what the API sends as `...Z`;
 * - a **day key** is a calendar day as `yyyy-MM-dd`. Keys compare correctly as plain strings, so a range test is a
 *   string comparison and no `Date` ever has to represent a "day".
 *
 * Nothing in this file reads the browser's zone: no `getHours`/`getDate`/`setHours`/`getTimezoneOffset`, no
 * `new Date(y, m, d)`. All zone knowledge comes from `Intl.DateTimeFormat` with an explicit `timeZone`, and all
 * calendar arithmetic is done on UTC fields, which have no zone. The spec proves that by making every
 * browser-local `Date` accessor throw while it replays the fixtures.
 *
 * Display formatting of instants (the `date` pipe) is deliberately out of scope: it still renders in the browser's
 * zone. A day key is shown with {@link formatDayKey} (the `dayKey` pipe), which never goes through a `Date`.
 */

/** A calendar day as `yyyy-MM-dd`. */
export type DayKey = string;

/** The zone used when the company zone is unknown. Matches the server default; never the browser's zone. */
export const DEFAULT_COMPANY_TIME_ZONE = 'Europe/Berlin';

/** A wall-clock time of day. */
export interface WallTime {
  hour: number;
  minute: number;
  second?: number;
  millisecond?: number;
}

/** An instant's wall clock in a zone: its day key plus the time of day. */
export interface CompanyWallClock extends Required<WallTime> {
  day: DayKey;
}

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const formatters = new Map<string, Intl.DateTimeFormat>();

/**
 * One cached formatter per zone. `en-US` + numeric parts so `formatToParts` yields plain digits regardless of the
 * user's locale. `hour12: false` can report midnight as hour `24` on some engines; {@link wallClockParts} maps it
 * back to `0` (the date part is already the new day in that case).
 */
function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

const pad = (value: number, length = 2): string => `${value}`.padStart(length, '0');

function toEpochMs(instant: Date | number): number {
  const ms = typeof instant === 'number' ? instant : instant.getTime();
  if (!Number.isFinite(ms)) throw new RangeError('company-day: invalid instant');
  return ms;
}

/** The zone's wall-clock fields of an instant, to the second. */
function wallClockParts(epochMs: number, timeZone: string) {
  const fields: Record<string, number> = {};
  for (const part of formatterFor(timeZone).formatToParts(new Date(epochMs))) {
    if (part.type !== 'literal') fields[part.type] = Number(part.value);
  }
  return {
    year: fields['year'],
    month: fields['month'],
    day: fields['day'],
    hour: fields['hour'] === 24 ? 0 : fields['hour'],
    minute: fields['minute'],
    second: fields['second']
  };
}

/** The zone's UTC offset at an instant, in milliseconds (positive east of Greenwich). */
function offsetMs(epochMs: number, timeZone: string): number {
  const p = wallClockParts(epochMs, timeZone);
  const wallAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // Compare whole seconds: the formatter drops milliseconds.
  return wallAsUtc - Math.floor(epochMs / 1000) * 1000;
}

/** Splits a day key into numbers, rejecting anything that is not a real `yyyy-MM-dd` calendar date. */
export function parseDayKey(key: DayKey): { year: number; month: number; day: number } {
  const match = DAY_KEY.exec(key ?? '');
  if (!match) throw new RangeError(`company-day: not a yyyy-MM-dd day key: '${key}'`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const check = new Date(Date.UTC(year, month - 1, day));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) {
    throw new RangeError(`company-day: not a calendar date: '${key}'`);
  }
  return {year, month, day};
}

/** True when the value is a valid `yyyy-MM-dd` calendar date. */
export function isDayKey(value: unknown): value is DayKey {
  if (typeof value !== 'string') return false;
  try {
    parseDayKey(value);
    return true;
  } catch {
    return false;
  }
}

/** Builds a day key from calendar numbers (month 1-12). Overflowing days/months roll over like `Date.UTC`. */
export function dayKeyOf(year: number, month: number, day: number): DayKey {
  const d = new Date(Date.UTC(year, month - 1, day));
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** True when the id is an IANA zone this engine knows. */
export function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== 'string' || timeZone.trim() === '') return false;
  try {
    formatterFor(timeZone);
    return true;
  } catch {
    return false;
  }
}

/** The company calendar day (`yyyy-MM-dd`) an instant falls on. */
export function companyDayKey(instant: Date | number, timeZone: string): DayKey {
  const p = wallClockParts(toEpochMs(instant), timeZone);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
}

/** The instant's wall clock in the company zone (day key + time of day, milliseconds carried over). */
export function companyWallClock(instant: Date | number, timeZone: string): CompanyWallClock {
  const epochMs = toEpochMs(instant);
  const p = wallClockParts(epochMs, timeZone);
  return {
    day: `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`,
    hour: p.hour,
    minute: p.minute,
    second: p.second,
    millisecond: ((epochMs % 1000) + 1000) % 1000
  };
}

/** Today in the company zone. `now` is injectable for tests; it defaults to the current instant. */
export function companyToday(timeZone: string, now: Date | number = Date.now()): DayKey {
  return companyDayKey(now, timeZone);
}

/** Pure calendar arithmetic: the key `n` days after (or before, for negative `n`) `key`. No zone involved. */
export function addCalendarDays(key: DayKey, n: number): DayKey {
  const {year, month, day} = parseDayKey(key);
  return dayKeyOf(year, month, day + n);
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function calendarDaysBetween(from: DayKey, to: DayKey): number {
  const a = parseDayKey(from);
  const b = parseDayKey(to);
  return Math.round((Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / MS_PER_DAY);
}

/** ISO weekday of a calendar day with Monday = 0 … Sunday = 6 (the app's week starts on Monday). */
export function mondayBasedWeekday(key: DayKey): number {
  const {year, month, day} = parseDayKey(key);
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

/** The first day of the key's month. */
export function startOfMonth(key: DayKey): DayKey {
  const {year, month} = parseDayKey(key);
  return dayKeyOf(year, month, 1);
}

/** The last day of the key's month. */
export function endOfMonth(key: DayKey): DayKey {
  const {year, month} = parseDayKey(key);
  return dayKeyOf(year, month + 1, 0);
}

/** The key's month shifted by `n` months, clamped to the first day (e.g. for "last month"). */
export function addCalendarMonths(key: DayKey, n: number): DayKey {
  const {year, month} = parseDayKey(key);
  return dayKeyOf(year, month + n, 1);
}

/**
 * The UTC instant of a company-local wall-clock time on a calendar day.
 *
 * Resolution follows the `compatible` rule (Temporal / `java.time`): a time repeated when the clocks go back maps
 * to its EARLIER occurrence; a time skipped when the clocks go forward is moved forward by the gap (Berlin 02:30
 * on the spring-forward day becomes 03:30 CEST). Computed from the zone's offsets a day either side, so it never
 * consults the browser's zone.
 */
export function companyLocalTimeInstant(key: DayKey, time: WallTime, timeZone: string): Date {
  const {year, month, day} = parseDayKey(key);
  const wallAsUtc = Date.UTC(year, month - 1, day, time.hour, time.minute, time.second ?? 0, time.millisecond ?? 0);

  const before = offsetMs(wallAsUtc - MS_PER_DAY, timeZone);
  const after = offsetMs(wallAsUtc + MS_PER_DAY, timeZone);
  const candidates = [wallAsUtc - before, wallAsUtc - after];
  // A candidate is real when the zone's own offset at that instant maps it back onto the requested wall clock.
  const valid = candidates.filter(c => offsetMs(c, timeZone) === wallAsUtc - c).sort((a, b) => a - b);

  // Ambiguous (fall back): the earlier instant. Gap (spring forward): the pre-transition offset, i.e. later.
  return new Date(valid.length > 0 ? valid[0] : wallAsUtc - before);
}

/** The UTC instant at which a company calendar day begins (local 00:00; the first instant of the day in a gap). */
export function companyDayStartInstant(key: DayKey, timeZone: string): Date {
  return companyLocalTimeInstant(key, {hour: 0, minute: 0}, timeZone);
}

/**
 * True when the instant's company day lies in `[from, to]`, both ends INCLUSIVE calendar days. A missing bound is
 * unbounded. This is the one range test every client-side day filter uses.
 */
export function isInCompanyDayRange(instant: Date | number, from: DayKey | null | undefined,
                                    to: DayKey | null | undefined, timeZone: string): boolean {
  const key = companyDayKey(instant, timeZone);
  if (from && key < from) return false;
  if (to && key > to) return false;
  return true;
}

const DATE_TIME_LOCAL = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/;

/**
 * The value an `<input type="datetime-local">` shows for an instant: its COMPANY wall clock, `yyyy-MM-ddTHH:mm`.
 * The native input has no zone of its own; reading `Date` local fields here would show the admin's browser clock
 * (an event at 20:00 Berlin would read 19:00 in a London browser). `''` for a missing instant.
 */
export function toDateTimeLocalValue(instant: Date | number | null | undefined, timeZone: string): string {
  if (instant === null || instant === undefined) return '';
  const wall = companyWallClock(instant, timeZone);
  return `${wall.day}T${pad(wall.hour)}:${pad(wall.minute)}`;
}

/**
 * The instant an `<input type="datetime-local">` value means, read as a COMPANY wall clock (DST resolved like
 * {@link companyLocalTimeInstant}). `undefined` for an empty or malformed value, so a cleared input never becomes
 * `Invalid Date`.
 */
export function parseDateTimeLocalValue(value: string | null | undefined, timeZone: string): Date | undefined {
  const match = DATE_TIME_LOCAL.exec(value ?? '');
  if (!match || !isDayKey(match[1])) return undefined;
  const hour = Number(match[2]);
  const minute = Number(match[3]);
  if (hour > 23 || minute > 59) return undefined;
  return companyLocalTimeInstant(match[1], {
    hour,
    minute,
    second: match[4] ? Number(match[4]) : 0,
    millisecond: match[5] ? Number(match[5].padEnd(3, '0')) : 0
  }, timeZone);
}

/**
 * The day key a browser-local calendar `Date` stands for (a date-picker cell or a `new Date(y, m, d)`), read from
 * its local fields. This is NOT an instant conversion: it names the calendar date the user SEES on a picker that
 * is built from browser-local dates, so it can be compared with {@link companyDayKey} values.
 */
export function pickerDateToDayKey(date: Date): DayKey {
  return dayKeyOf(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/**
 * A browser-local `Date` at 00:00 of a day key, for DISPLAY only (the `date` pipe and date-picker grids work on
 * browser-local dates). Never send it to the server and never compare it as an instant.
 */
export function dayKeyToPickerDate(key: DayKey): Date {
  const {year, month, day} = parseDayKey(key);
  return new Date(year, month - 1, day);
}

const GERMAN_MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September',
  'Oktober', 'November', 'Dezember'];

/**
 * A calendar day as German users read it, built from the key's own digits: `'short'` gives `30.09.2026`,
 * `'long'` gives `30. September 2026`. No `Date` is involved, so the day shown is the day stored whatever zone the
 * browser is in. (The `date` pipe reads a `yyyy-MM-dd` string as browser-local midnight, which happens to show the
 * same day, but it cannot run in a spec that disables the browser-local accessors.)
 *
 * @returns the formatted day, or `–` for anything that is not a `yyyy-MM-dd` day. An instant, which a server built
 * before a field became a day still sends, is not guessed at.
 */
export function formatDayKey(value: unknown, style: 'short' | 'long' = 'short'): string {
  if (!isDayKey(value)) return '–';
  const {year, month, day} = parseDayKey(value);
  return style === 'long'
    ? `${pad(day)}. ${GERMAN_MONTHS[month - 1]} ${pad(year, 4)}`
    : `${pad(day)}.${pad(month)}.${pad(year, 4)}`;
}
